import CheckIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DocumentIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import MapIcon from '@mui/icons-material/Map';
import UploadIcon from '@mui/icons-material/CloudUpload';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { CustomUploadComponentProps, UploadingFile } from 'src/interfaces/FileManager';
import { MAX_FILES_PER_UPLOAD } from 'src/common/constants/uploadLimits';
import { compressImagesForUpload } from 'src/helpers/compressImageForUpload';
import { useUploadThing } from 'src/helpers/uploadThing';

import { getUploadAcceptConfig } from './uploadAccept';
import { validateUploadBatchSize } from './uploadBatchUtils';

const CustomUploadComponent = ({
  endpoint,
  onUploadComplete,
  onUploadError,
  onReset,
}: CustomUploadComponentProps) => {
  const { t } = useTranslation();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const uploadingFilesRef = useRef(uploadingFiles);
  uploadingFilesRef.current = uploadingFiles;

  const { startUpload, isUploading } = useUploadThing(endpoint, {
    onClientUploadComplete: res => {
      setUploadingFiles(prev =>
        prev.map(file =>
          file.status === 'uploading' ? { ...file, status: 'completed' as const, progress: 100 } : file,
        ),
      );
      onUploadComplete(res || []);
    },
    onUploadError: error => {
      setUploadingFiles(prev =>
        prev.map(file => (file.status === 'uploading' ? { ...file, status: 'error' as const } : file)),
      );
      onUploadError(error instanceof Error ? error : new Error(String(error)));
    },
  });

  useEffect(() => {
    return () => {
      uploadingFilesRef.current.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const validation = validateUploadBatchSize(acceptedFiles.length);
      if (!validation.valid) {
        onUploadError(new Error(t('form.upload.tooManyFiles', { max: validation.max })));
        return;
      }

      let filesToUpload = acceptedFiles;
      if (endpoint === 'image') {
        setIsCompressing(true);
        try {
          filesToUpload = await compressImagesForUpload(acceptedFiles);
        } finally {
          setIsCompressing(false);
        }
      }

      const newFiles: UploadingFile[] = filesToUpload.map(file => {
        const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined;
        return {
          file,
          progress: 0,
          status: 'uploading',
          preview,
        };
      });

      setUploadingFiles(prev => [...prev, ...newFiles]);
      startUpload(filesToUpload);
    },
    [endpoint, onUploadError, startUpload, t],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: getUploadAcceptConfig(endpoint),
    multiple: true,
    disabled: isUploading || isCompressing,
  });

  const removeFile = (index: number) => {
    setUploadingFiles(prev => {
      const newFiles = [...prev];
      const file = newFiles[index];
      if (!file) return newFiles;
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const clearCompletedFiles = useCallback(() => {
    setUploadingFiles(prev => {
      prev.forEach(file => {
        if (file.status === 'completed' && file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      return prev.filter(file => file.status !== 'completed');
    });
  }, []);

  const getFileIcon = (_file: File) => {
    switch (endpoint) {
      case 'image':
        return <ImageIcon sx={{ fontSize: 40 }} />;
      case 'document':
        return <DocumentIcon sx={{ fontSize: 40 }} />;
      case 'trackMap':
        return <MapIcon sx={{ fontSize: 40 }} />;
      default:
        return <DocumentIcon sx={{ fontSize: 40 }} />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {isCompressing
            ? t('form.upload.compressing')
            : isDragActive
              ? t('form.upload.dropFiles')
              : t('form.upload.selectFiles')}
        </Typography>
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {t(`form.upload.${endpoint}.supportedFormats`)}
        </Typography>
        <Typography variant="caption" sx={{
          color: "text.secondary",
          display: 'block',
          mt: 0.5,
        }}>
          {t('form.upload.maxFilesPerBatch', { max: MAX_FILES_PER_UPLOAD })}
        </Typography>
      </Box>
      {uploadingFiles.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('form.upload.uploadingFiles')} ({uploadingFiles.length})
          </Typography>
          <Stack spacing={1}>
            {uploadingFiles.map((uploadingFile, index) => (
              <Card key={index} sx={{ position: 'relative' }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        minWidth: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {uploadingFile.preview ? (
                        <img
                          src={uploadingFile.preview}
                          alt={uploadingFile.file.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      ) : (
                        getFileIcon(uploadingFile.file)
                      )}
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {uploadingFile.file.name}
                      </Typography>
                      <Typography variant="caption" sx={{
                        color: "text.secondary"
                      }}>
                        {formatFileSize(uploadingFile.file.size)}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={uploadingFile.progress}
                          color={uploadingFile.status === 'error' ? 'error' : 'primary'}
                          sx={{ height: 4, borderRadius: 2 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {uploadingFile.status === 'completed' && <CheckIcon color="success" />}
                      {uploadingFile.status === 'error' && (
                        <Chip label={t('form.upload.error')} color="error" size="small" />
                      )}
                      <IconButton
                        size="small"
                        onClick={() => removeFile(index)}
                        disabled={uploadingFile.status === 'uploading'}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
          <Box sx={{ mt: 2, textAlign: 'center', display: 'flex', gap: 1, justifyContent: 'center' }}>
            {uploadingFiles.some(file => file.status === 'completed') && (
              <Button
                variant="outlined"
                size="small"
                onClick={clearCompletedFiles}
                startIcon={<CheckIcon />}
              >
                {t('form.upload.clearCompleted')}
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={() => onReset?.()} color="secondary">
              Reset Upload
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomUploadComponent;
